// src/utils/tokenManager.ts - 完整版（支持过期时间预刷新，统一判断逻辑）
class TokenManager {
  private appVersion: string;
  private accessToken: string | null = null;
  private expiresUtc: Date | null = null; // token过期时间
  private refreshToken: string | null = null;
  private isRefreshing = false;
  private queue: Array<{ resolve: (t: string) => void; reject: (e: any) => void }> = [];

  private xhrPatched = false;
  // 关键：定义白名单路径（不需要 token 的接口）
  private whiteListPaths = [
    '/api/login', // 登录
    '/api/login/refresh', // 刷新 token（加入白名单，跳过过期判断）
    '/api/login/CloudVerify', // 其他公开接口
    '/api/Share/Access',
    '/getaddress/ip/local/geo/v1/district',
  ];

  // 提前刷新时间（毫秒）
  private readonly PRE_REFRESH_MS = 0 * 60 * 1000; // 提前60秒刷新
  // 动态生成存储Key：pacs_access_版本号（例如 pacs_access_0.0.1）
  public get STORAGE_KEY(): string {
    return `mobile_pacs_access_${this.appVersion}`; // 替换点号避免特殊字符问题，可选
    // 若不需要替换点号，直接 return `pacs_access_${this.appVersion}`;
  }

  constructor() {
    this.appVersion = (window as any).config?.version || '1.0.0'; // 兜底版本号
    this.loadToken();
    this.installAllInterceptors();
  }

  public loadToken() {
    const storageStr = localStorage.getItem(this.STORAGE_KEY);
    if (storageStr) {
      try {
        const storageObj = JSON.parse(storageStr);
        this.accessToken = storageObj.atk_v2 || null;
        this.refreshToken = storageObj.refreshToken || null;
        if (storageObj.atk_expires) {
          this.expiresUtc = new Date(storageObj.atk_expires);
        } else {
          this.parseExpiryFromToken();
        }
      } catch (e) {}
    }
  }

  /** 从JWT token中解析过期时间（备用方案） */
  private parseExpiryFromToken() {
    if (!this.accessToken) return;

    try {
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      if (payload.exp) {
        // JWT exp字段是秒，转成毫秒
        this.expiresUtc = new Date(payload.exp * 1000);
        // 更新存储对象中的过期时间
        this.updateStorage(obj => {
          obj.atk_expires = this.expiresUtc?.toISOString() || null;
        });
      }
    } catch (e) {
      console.warn('JWT 解析失败，无法获取过期时间');
    }
  }

  private updateStorage(
    updater: (obj: {
      atk_v2: string | null;
      atk_expires: string | null;
      refreshToken: string | null;
    }) => void
  ) {
    // 先读取现有对象，若无则初始化空对象
    const storageStr =
      localStorage.getItem(this.STORAGE_KEY) ||
      '{"atk_v2":null,"atk_expires":null,"refreshToken":null}';
    try {
      const storageObj = JSON.parse(storageStr);
      // 执行更新逻辑
      updater(storageObj);
      // 保存回localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storageObj));
    } catch (e) {
      console.error(`更新${this.STORAGE_KEY}失败`, e);
    }
  }

  //拦截ohif的请求
  private installXHRInterceptor() {
    // 防止重复拦截
    if (this.xhrPatched) {
      return;
    }

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    const origSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
    // 可选：用一个 WeakMap 防止重复处理同一个 XHR
    const processedXHR = new WeakMap();
    const self = this;

    // 存储原始请求头
    const requestHeaders = new WeakMap<XMLHttpRequest, Map<string, string>>();

    // 用于存储 token 刷新的 Promise，防止重复刷新
    let refreshTokenPromise: Promise<string> | null = null;

    // 统一的过期判断逻辑：提前1分钟判定是否过期（和fetch保持一致）
    const isTokenExpired = () => {
      // 无 token → 不判定为“过期”，直接返回 false（避免触发 refresh）
      if (!self.accessToken || !self.expiresUtc) return false;
      const nowTimestamp = new Date().getTime();
      const nowPlus1Minute = new Date(nowTimestamp + self.PRE_REFRESH_MS); // 改用常量，便于维护
      return nowPlus1Minute >= self.expiresUtc;
    };

    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...args: any[]) {
      // 保存 URL 和请求头存储
      const urlStr = url instanceof URL ? url.toString() : url;
      (this as any)._requestUrl = urlStr;
      requestHeaders.set(this, new Map<string, string>());

      return origOpen.call(this, method, url, ...args);
    };

    // 重写 setRequestHeader 以捕获所有设置的头部
    XMLHttpRequest.prototype.setRequestHeader = function (name: string, value: string) {
      const headers = requestHeaders.get(this);
      if (headers) {
        headers.set(name.toLowerCase(), value);
      }
      return origSetRequestHeader.call(this, name, value);
    };

    XMLHttpRequest.prototype.send = async function (body) {
      const url = (this as any)._requestUrl;
      const xhr = this;

      // 只处理非白名单的请求（/api/login/refresh 属于白名单，直接跳过后续逻辑）
      if (url && !self.isWhiteListRequest(url)) {
        // 步骤1：检查 token 是否过期，若过期则先刷新
        if (isTokenExpired() && self.getCurrentToken()) {
          // 防止重复刷新：如果已有刷新请求，复用这个 Promise
          if (!refreshTokenPromise) {
            refreshTokenPromise = self
              .refreshToken_func()
              .catch(error => {
                console.warn('Token 刷新失败:', error);
                throw error; // 抛出错误，让请求捕获
              })
              .finally(() => {
                // 刷新完成后重置 Promise
                refreshTokenPromise = null;
              });
          }

          // 等待 token 刷新完成再继续
          try {
            await refreshTokenPromise;
          } catch (error) {
            // 刷新失败时，可根据需求处理（比如跳转到登录页）
            console.error('Token 刷新失败，请求终止:', url);
            // 这里可以手动终止 XHR（可选）
            xhr.abort();
            return; // 终止 send 执行
          }
        }

        // 步骤2：注入/更新 Authorization 头
        const currentToken = self.getCurrentToken();
        if (currentToken) {
          const headers = requestHeaders.get(xhr);
          const hasAuthHeader = headers && headers.has('authorization');

          if (!hasAuthHeader) {
            // 没有认证头，注入新的 token
            origSetRequestHeader.call(xhr, 'Authorization', `Bearer ${currentToken}`);
          } else {
            // 有认证头，检查是否需要更新
            const existingAuthHeader = headers.get('authorization');
            const existingToken = existingAuthHeader?.replace('Bearer ', '');

            if (existingToken !== currentToken) {
              origSetRequestHeader.call(xhr, 'Authorization', `Bearer ${currentToken}`);
            }
          }
        } else {
          console.warn('XHR 请求需要 token，但当前 token 为空:', url);
        }
      }

      // 执行原始的 send 方法（白名单接口直接执行，不处理 token）
      return origSend.call(xhr, body);
    };

    // 监听 XHR 状态变化，移除401中的刷新逻辑（仅保留日志，可选）
    const origAddEventListener = XMLHttpRequest.prototype.addEventListener;
    XMLHttpRequest.prototype.addEventListener = function (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions
    ) {
      if (type === 'readystatechange') {
        const wrappedListener = (event: Event) => {
          if (this.readyState === 4) {
            const url = (this as any)._requestUrl;
            const status = this.status;

            if (status === 401 && url && !self.isWhiteListRequest(url)) {
              console.log('XHR 请求返回 401，已通过前置刷新处理，此处仅作日志:', url);
              // 移除原有刷新逻辑，因为前置已经处理
            }
          }
        };

        return origAddEventListener.call(this, type, wrappedListener, options);
      }

      return origAddEventListener.call(this, type, listener, options);
    };

    this.xhrPatched = true;
  }

  /** 设置token和过期时间 */
  setToken(token: string, expiresUtc?: string | Date, refreshToken?: string) {
    this.accessToken = token; // 同步更新内存中的 accessToken
    if (refreshToken) {
      this.refreshToken = refreshToken; // 同步更新 refreshToken
    }
    // 更新存储对象
    this.updateStorage(obj => {
      obj.atk_v2 = token;
      if (refreshToken) {
        obj.refreshToken = refreshToken;
      }
      if (expiresUtc) {
        const expiryDate = typeof expiresUtc === 'string' ? new Date(expiresUtc) : expiresUtc;
        obj.atk_expires = expiryDate.toISOString();
      }
    });

    // 更新内存中的过期时间
    if (expiresUtc) {
      const expiryDate = typeof expiresUtc === 'string' ? new Date(expiresUtc) : expiresUtc;
      this.expiresUtc = expiryDate;
    } else {
      this.parseExpiryFromToken();
    }
  }

  getCurrentToken(): string | null {
    return this.accessToken;
  }

  clearToken() {
    this.accessToken = null;
    this.expiresUtc = null;
    this.xhrPatched = false;
    localStorage.removeItem(this.STORAGE_KEY);

    // if (location.pathname !== '/login') location.href = '/login';
  }

  /** 关键改进：更严格的白名单判断（/api/login/refresh 已加入白名单） */
  private isWhiteListRequest(url: string): boolean {
    if (!url) return false;

    // 转换为绝对 URL，便于比较
    let fullUrl = url;
    if (!url.startsWith('http')) {
      const base = (window as any).config?.servers?.baseUrl || '';
      fullUrl = base + url;
    }

    // 检查是否在白名单中（直接匹配，不再排除 /api/login/refresh）
    return this.whiteListPaths.some(path => fullUrl.includes(path));
  }

  private async refreshToken_func() {
    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => this.queue.push({ resolve, reject }));
    }

    this.isRefreshing = true;

    try {
      const base = (window as any).config?.servers?.baseUrl || '';
      const formData = new FormData();
      const storageStr = localStorage.getItem(this.STORAGE_KEY);
      const oldRefreshToken = storageStr ? JSON.parse(storageStr).refreshToken : null;
      if (oldRefreshToken) formData.append('refreshToken', oldRefreshToken);

      const res = await fetch(`${base}/api/login/refresh`, { method: 'POST', body: formData });

      if (!res.ok) {
        // 先读取错误信息
        const errorText = await res.text();
        console.error('刷新失败，响应内容:', errorText);
        throw new Error(`Refresh failed: ${res.status} ${res.statusText}`);
      }
      // 只调用一次 json()，并保存结果
      const responseData = await res.json();

      const { accessToken, accessExpiresUtc, refreshToken } = responseData;
      if (!accessToken) {
        throw new Error('刷新响应中没有 accessToken');
      }

      // 设置新的token和过期时间
      this.setToken(accessToken, accessExpiresUtc, refreshToken);

      this.queue.forEach(p => p.resolve(accessToken));
      this.queue = [];
      return accessToken;
    } catch (e) {
      console.error('刷新异常:', e);
      this.clearToken();
      this.queue.forEach(p => p.reject(e));
      this.queue = [];
      throw e;
    } finally {
      this.isRefreshing = false;
    }
  }

  /** 全局 fetch 拦截器 - 改造后：/api/login/refresh 跳过过期判断 */
  private installFetchInterceptor() {
    const orig = window.fetch;
    const self = this;

    // 用于存储 token 刷新的 Promise，防止重复刷新（和XHR隔离，也可全局共享）
    let refreshTokenPromise: Promise<string> | null = null;

    // 统一的过期判断逻辑：和XHR保持一致（提前1分钟判定）
    const isTokenExpired = () => {
      if (!self.expiresUtc) return true;
      const nowTimestamp = new Date().getTime();
      const nowPlus1Minute = new Date(nowTimestamp + self.PRE_REFRESH_MS);
      return nowPlus1Minute >= self.expiresUtc;
    };

    window.fetch = async (input: RequestInfo, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input.url;

      // 关键：白名单接口（含/api/login/refresh）直接放行，不带 token、不判断过期
      if (self.isWhiteListRequest(url)) {
        return orig(input, {
          ...init,
          credentials: 'include',
          // 确保白名单接口不带 Authorization 头
          headers: this.cleanAuthHeader(init?.headers),
        });
      }

      // 非白名单接口：先检查token是否过期，过期则刷新
      if (isTokenExpired() && self.getCurrentToken()) {
        // 防止重复刷新
        if (!refreshTokenPromise) {
          refreshTokenPromise = self
            .refreshToken_func()
            .catch(error => {
              throw error;
            })
            .finally(() => {
              refreshTokenPromise = null;
            });
        }

        // 等待刷新完成
        try {
          await refreshTokenPromise;
        } catch (error) {
          console.error('Token 刷新失败，fetch请求终止:', url);
          // 抛出错误让业务层处理，或返回空响应
          throw new Error('Token refresh failed, fetch aborted');
        }
      }

      // 注入token
      const token = self.getCurrentToken();
      const headers = new Headers(init?.headers || {});
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // 执行原始fetch请求（移除401后的刷新逻辑，因为前置已经处理）
      const resp = await orig(input, { ...init, headers, credentials: 'include' });

      // 可选：保留401日志（如需自动重试可在此添加，但前置已处理，一般不需要）
      if (resp.status === 401 && token) {
        console.log('Fetch 请求返回 401，前置刷新逻辑未覆盖，可手动处理:', url);
      }

      return resp;
    };
  }

  // 辅助方法：清除 Authorization 头
  private cleanAuthHeader(headers?: HeadersInit): HeadersInit {
    if (!headers) return {};

    const newHeaders = new Headers(headers);
    newHeaders.delete('Authorization');
    return newHeaders;
  }

  private installAllInterceptors() {
    this.installFetchInterceptor();
    this.installXHRInterceptor(); // 添加这一行
  }

  // 提供白名单管理方法（可选）
  addWhiteListPath(path: string) {
    if (!this.whiteListPaths.includes(path)) {
      this.whiteListPaths.push(path);
    }
  }

  removeWhiteListPath(path: string) {
    this.whiteListPaths = this.whiteListPaths.filter(p => p !== path);
  }
}

// 启动即生效
const instance = new TokenManager();

// 添加页面可见性变化监听（移除刷新逻辑，如需保留可注释）
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // 移除原手动刷新逻辑
    }
  });

  window.addEventListener('storage', e => {
    if (e.key === instance.STORAGE_KEY) {
      instance.loadToken();
    }
  });
}

(window as any).tokenManager = instance;
export default instance;
